/**
 * 🔐 MGI Authentication Store
 * State management para autenticación y autorización
 * Manejo de usuarios, roles y tokens JWT
 *
 * PR-23: UserRole canónico en snake_case — el backend envía snake_case
 *        y el frontend lo usa tal cual, sin transformación.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ApiClient } from '@/services/api/api-client'
// P12-TEST1A-FE4: validateToken/logout must use the CANONICAL runtime-config client. The
// extension-less `@/services/api-client` resolves to the `.js` compat shim (→ _deprecated
// localhost:3002); the explicit `.ts` binds the runtime-config axios instance.
import { apiClient } from '@/services/api-client.ts'
import { preloadCriticalData } from '@/services/api/preload'
// P13-V7B-MIN C2: the SINGLE canonical role→home authority. `router/guards.ts` imports this store in
// return, but only to call `useAuthStore()` INSIDE the guard at request time — it evaluates nothing
// from this module while loading — so the cycle resolves and `getRedirectForRole` (a pure function
// over a frozen map) is safe to consume here.
import { getRedirectForRole } from '@/router/guards'

// P12-TEST1A-FE4: user-facing login failure truth. Distinguishes invalid credentials (401) from a
// transport/connection failure (no HTTP response), without leaking transport details, tokens,
// headers, URLs, stack traces, or backend internals to the UI message.
function describeLoginFailure(err: any): string {
  const status = err?.response?.status
  if (status === 401) return 'Credenciales inválidas'
  if (!err?.response) return 'No se pudo conectar con el servidor'
  const backendMsg = err?.response?.data?.message
  return typeof backendMsg === 'string' && backendMsg.trim() ? backendMsg : 'No se pudo iniciar sesión'
}
import websocketService from '@/services/websocket.service'
import { useNotificationsStore } from '@/stores/notifications'
import { SELF_ROTATION_ENDPOINT, type SelfRotationOutcome } from '@/services/auth/selfCredentialRotation'

// 🎯 Tipos de usuario - Sistema empresarial MGI con 7 roles
export interface User {
  id: number
  username: string
  email: string
  fullName: string
  role: UserRole
  department?: string
  region?: string
  zone?: string
  agency?: string
  avatar?: string
  lastLogin?: string
  isActive: boolean
  permissions: string[]
  // P13-K3 · what the SERVER says this actor may do, projected by the same capability contract the
  // backend fences enforce. Optional because a frontend running against a backend that predates K3
  // simply receives nothing and therefore offers nothing.
  availableActions?: Array<{ capability: string; writerId?: string; boundary?: string } | string>
  assignableRoles?: string[]
  // P13-V7A · the actor's PERSISTED administration as the server reports it on /validate and /login
  // (`administrationId`). It is reference identity for the administration_manager landing — WHICH
  // administration to read — never authority: the server re-derives the partition on every request.
  administrationId?: number | null
  // 👥 Grupos asignados para Supervisor de Campo
  assignedGroups?: number[]
  // 🏢 Departamentos asignados
  assignedDepartments?: number[]
  // 🛣️ Ruta asignada para Operador de Rutas
  assignedRoute?: {
    id: number
    name: string
  }
}

const CANONICAL_USER_ROLES = [
  'super_admin',
  'administration_manager',
  'group_manager',
  'field_supervisor',
  'route_operator',
  'agency_employee',
  'department_head'
] as const

const CANONICAL_USER_ROLE_SET = new Set<string>(CANONICAL_USER_ROLES)

export type UserRole = typeof CANONICAL_USER_ROLES[number]

// 🔐 Datos de login
export interface LoginCredentials {
  email: string
  password: string
}

// 📊 Response de autenticación
export interface AuthResponse {
  token: string
  user: User
  expiresIn?: number
}

type SessionAuthority = 'anonymous' | 'restoring' | 'authenticated'

export const useAuthStore = defineStore('auth', () => {
  const TEST_SESSION_TOOLS_ENABLED = String((import.meta as any).env?.VITE_ENABLE_TEST_SESSION_TOOLS ?? '0') === '1'

  // P12-TEST1A-R3A: internal session-generation epoch — an ownership marker for async session
  // operations. It lives ONLY in this store-factory closure (one per Pinia instance): it is NOT
  // module-global, NOT component-local, NOT persisted to localStorage, NOT derived from the JWT,
  // and is NEVER exposed on the returned store (so it can't become a second authority source).
  // It advances whenever a genuinely NEW session is established (persistSession) or ANY session is
  // torn down (clearSessionState). Async termination captured under an old generation must never
  // clear or alter a newer session.
  let sessionGeneration = 0
  // Manual-logout terminations keyed by the generation that owned them, so a newer session's
  // logout can never reuse an older generation's in-flight remote POST (each authenticated
  // generation may own exactly one distinct /auth/logout request).
  const terminationsByGeneration = new Map<number, Promise<void>>()

  // P12-TEST1A-R3B: validation ownership is ALSO scoped by generation (replaces the former single
  // global `pendingSessionValidation`). A /auth/validate result — success, invalid, failure, or
  // cleanup — may only affect the exact generation that started it. Same-generation callers dedupe
  // onto one pending validation; a newer generation never reuses an older one's pending promise; a
  // stale result (its generation is no longer current) applies/clears NOTHING. No validation state
  // is persisted to localStorage, decoded from the JWT, or exposed as a second session authority.
  type ValidationOutcome = 'valid' | 'invalid' | 'stale'
  const validationsByGeneration = new Map<number, Promise<ValidationOutcome>>()
  const restoreValidationsByGeneration = new Map<number, Promise<boolean>>()

  // 🔄 Role validation: backend sends snake_case, validate and pass through
  // PR-23: Anteriormente convertía snake_case→PascalCase. Ahora snake_case es canónico.
  function mapBackendRoleToFrontend(backendRole: string): UserRole {
    const normalizedRole = String(backendRole || '').trim()
    if (!CANONICAL_USER_ROLE_SET.has(normalizedRole)) {
      throw new Error(`Rol no canónico recibido: ${backendRole}`)
    }

    return normalizedRole as UserRole
  }

  // 📊 Estado reactivo
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const tokenTimestamp = ref<number | null>(null)
  const sessionAuthority = ref<SessionAuthority>('anonymous')

  // 💾 Computed properties
  const hasSessionBootstrap = computed(() => !!token.value && !!user.value)
  const isAuthenticated = computed(() =>
    sessionAuthority.value === 'authenticated' && hasSessionBootstrap.value
  )
  const userRole = computed(() => user.value?.role || null)
  const userPermissions = computed(() => user.value?.permissions || [])
  const userName = computed(() => user.value?.fullName || user.value?.username || '')
  const session = computed(() => ({
    authority: sessionAuthority.value,
    isAuthenticated: isAuthenticated.value,
    hasBootstrap: hasSessionBootstrap.value,
    userId: user.value?.id ?? null,
    role: userRole.value,
    tokenTimestamp: tokenTimestamp.value
  }))

  // 🔐 Verificar si el usuario tiene un rol específico
  const hasRole = computed(() => (role: UserRole | string) => {
    const currentRole = userRole.value

    console.log(`🔍 hasRole check: comparing "${currentRole}" with "${role}"`)

    if (typeof role === 'string') {
      const result = currentRole === role
      console.log(`🔍 String comparison result: ${result}`)
      return result
    }

    const result = currentRole === role
    console.log(`🔍 UserRole comparison result: ${result}`)
    return result
  })

  // 🔓 Verificar permisos
  const hasPermission = computed(() => (permission: string) =>
    userPermissions.value.includes(permission)
  )

  // ── P13-K3 · server-driven capability projection ────────────────────────────────────────────
  //
  // These read the SAME contract the backend fences enforce, so the UI cannot offer an action the
  // server would refuse. They are advisory, never authority: every mutation still traverses a fence
  // server-side, on locked rows, inside a transaction. That is also why they are safe when absent —
  // no payload means no actions, which is why the two repositories may merge in either order.
  const availableActions = computed(() => user.value?.availableActions || [])
  const assignableRoles = computed(() => user.value?.assignableRoles || [])

  /** True only when the server listed this capability for this actor. No role fallback. */
  const canPerform = computed(() => (capability: string) =>
    availableActions.value.some((a: any) => (typeof a === 'string' ? a : a?.capability) === capability)
  )

  /** True only when the server listed the role among those this actor may grant. */
  const canAssignRole = computed(() => (role: string) => assignableRoles.value.includes(role))

  // 🎯 Verificar acceso a dashboard
  const canAccessDashboard = computed(() => (dashboardType: UserRole) => {
    if (!isAuthenticated.value) return false

    if (userRole.value === 'super_admin') return true

    return userRole.value === dashboardType
  })

  // 🏢 Verificar si es rol administrativo
  const isAdminRole = computed(() =>
    ['super_admin', 'group_manager'].includes(userRole.value || '')
  )

  // 🔧 Verificar si es rol operativo
  const isOperationalRole = computed(() =>
    ['field_supervisor', 'route_operator', 'agency_employee'].includes(userRole.value || '')
  )

  // 📊 Verificar si es rol analítico
  const isAnalyticalRole = computed(() =>
    userRole.value === 'department_head'
  )

  // 🗺️ Ruta de dashboard por rol — P13-V7B-MIN C2: SUBORDINADA a la autoridad canónica.
  //
  // This getter used to carry its OWN role→home table. It agreed with `getRedirectForRole` by
  // coincidence, not by construction, and nothing prevented the two from drifting — which is exactly
  // how fresh login, `/dashboard` and session restore could land the same role in different places.
  // The table is gone; the getter now delegates, so every remaining consumer receives the canonical
  // answer. It is kept (rather than deleted) so that any consumer not reached by this mission is
  // corrected rather than broken.
  const getDashboardRoute = computed(() => getRedirectForRole(userRole.value || ''))

  // PR-02: Helper centralizado para mapear backend user → frontend User
  function mapBackendUserToFrontend(backendUser: any): User {
    return {
      id: backendUser.id,
      username: backendUser.email || '',
      email: backendUser.email || '',
      fullName: backendUser.name || backendUser.fullName || '',
      role: mapBackendRoleToFrontend(backendUser.role as string),
      department: undefined,
      isActive: true,
      // P13-K3 · the server's answer is KEPT instead of being overwritten with []. This field was
      // hardcoded empty, so `hasPermission` returned false for everything and every permission-gated
      // arm of the navigation was dead code — visibility silently fell through to the role
      // fallbacks, and the UI ended up driven by a local role string rather than by what the server
      // would actually allow. An absent payload still yields [], so the store stays fail-closed and
      // is safe against a backend that does not send the field yet.
      permissions: Array.isArray(backendUser.permissions) ? backendUser.permissions : [],
      availableActions: Array.isArray(backendUser.availableActions) ? backendUser.availableActions : [],
      assignableRoles: Array.isArray(backendUser.assignableRoles) ? backendUser.assignableRoles : [],
      // P13-V7A · KEPT verbatim (it used to be dropped here): a positive integer or null, nothing else.
      administrationId: Number.isInteger(backendUser.administrationId) && backendUser.administrationId > 0
        ? backendUser.administrationId
        : null,
      assignedGroups: backendUser.assignedGroups || [],
      assignedDepartments: backendUser.assignedDepartments || [],
    } as User
  }

  function persistSession(sessionToken: string, mappedUser: User) {
    // P12-TEST1A-R3A: a genuinely new authenticated session — advance the generation BEFORE it
    // becomes authoritative so no prior session's async termination can own, reuse, or clear it.
    sessionGeneration++
    token.value = sessionToken
    user.value = mappedUser
    sessionAuthority.value = 'authenticated'

    localStorage.setItem('mgi_auth_token', sessionToken)
    localStorage.setItem('mgi_user_data', JSON.stringify(mappedUser))
    tokenTimestamp.value = Date.now()
    localStorage.setItem('mgi_token_timestamp', String(tokenTimestamp.value))
  }

  function clearSessionState() {
    // P12-TEST1A-R3A: teardown invalidates the current generation, so any async work still
    // captured under it (e.g. an in-flight logout POST from this session) can no longer clear or
    // resurrect a future session established afterward.
    sessionGeneration++
    user.value = null
    token.value = null
    error.value = null
    tokenTimestamp.value = null
    sessionAuthority.value = 'anonymous'

    localStorage.removeItem('mgi_auth_token')
    localStorage.removeItem('mgi_user_data')
    localStorage.removeItem('mgi_token_timestamp')
    websocketService.disconnect()
  }

  // PR-02: Helper para procesar respuesta de login/impersonación y setear estado
  function applySessionResponse(payload: any): boolean {
    if (!payload?.token || !payload?.user) return false
    const mappedUser = mapBackendUserToFrontend(payload.user)
    persistSession(payload.token, mappedUser)
    return true
  }

  function applyValidatedSession(payload: any): boolean {
    if (!payload?.user || !token.value) return false
    const mappedUser = mapBackendUserToFrontend(payload.user)
    user.value = mappedUser
    sessionAuthority.value = 'authenticated'
    localStorage.setItem('mgi_user_data', JSON.stringify(mappedUser))
    return true
  }

  function ensureRestoredSessionValidated(): Promise<boolean> {
    if (sessionAuthority.value !== 'restoring') {
      return Promise.resolve(isAuthenticated.value)
    }

    // R3B: scope the restore-validation to the generation that owns the 'restoring' bootstrap, so a
    // newer session never reuses it and a stale settle never clears another generation's ownership.
    const generation = sessionGeneration
    const existing = restoreValidationsByGeneration.get(generation)
    if (existing) {
      return existing
    }

    const run = validateSession()
      .then(outcome => {
        if (outcome === 'valid') {
          // Only arm realtime for THIS generation while it is still current; a bootstrap that was
          // replaced/torn down before validation resolved must not be re-armed by a late success.
          if (sessionGeneration === generation) {
            try {
              websocketService.connect().then(() => {
                if (user.value) websocketService.subscribeUserChannels(user.value)
                const notif = useNotificationsStore()
                notif.initialize().catch(() => {})
              })
            } catch {}
          }
          return true
        }

        // R3B micro-repair: only a genuine invalid-CURRENT validation actually cleared a session, so
        // only it may log the truthful "sesión limpiada". A 'stale' outcome means this restoring
        // bootstrap was superseded before its validation resolved — it cleared NOTHING, so it must
        // not claim it did.
        if (outcome === 'invalid') {
          console.warn('⚠️ Bootstrap local rechazado por validate, sesión limpiada')
        } else {
          console.debug('ℹ️ Validación de bootstrap obsoleta ignorada; la sesión ya fue reemplazada')
        }
        return false
      })
      .catch(() => false)
      .finally(() => {
        // Remove ONLY this generation's record (generations are monotonic and never reused), so a
        // stale restore-validation settling cannot delete a newer generation's pending ownership.
        restoreValidationsByGeneration.delete(generation)
      })

    restoreValidationsByGeneration.set(generation, run)
    return run
  }

  function schedulePostLoginPreload(delayMs = 1000) {
    // Los shells operativos cargan datos self-scoped propios y no deben disparar
    // la precarga analítica compartida que consulta /analytics/kpis.
    if (isOperationalRole.value) {
      return
    }

    setTimeout(async () => {
      try { await preloadCriticalData() } catch {}
    }, delayMs)
  }

  // 🔑 Función de Login
  async function login(credentials: LoginCredentials): Promise<boolean> {
    try {
      isLoading.value = true
      error.value = null

      const response = await ApiClient.post<AuthResponse>('/auth/login', credentials)
      const payload = response?.data

      if (applySessionResponse(payload)) {
        try {
          await websocketService.connect()
          if (user.value) websocketService.subscribeUserChannels(user.value)
          const notif = useNotificationsStore()
          notif.initialize().catch(() => {})
        } catch (_e) {
          console.warn('⚠️ No se pudo conectar WebSocket tras login')
        }

        schedulePostLoginPreload()

        return true
      }

      error.value = 'Respuesta inválida del servidor'
      return false
    } catch (err: any) {
      error.value = err.message || 'Error en el login'
      console.error('❌ Error de login:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Login real forzado (siempre /auth/login)
  async function loginReal(credentials: LoginCredentials): Promise<boolean> {
    try {
      isLoading.value = true
      error.value = null
      const response = await ApiClient.post<AuthResponse>('/auth/login', credentials)
      const payload = response?.data

      if (applySessionResponse(payload)) {
        try {
          await websocketService.connect()
          if (user.value) websocketService.subscribeUserChannels(user.value)
          const notif = useNotificationsStore()
          notif.initialize().catch(() => {})
        } catch {}

        schedulePostLoginPreload()

        return true
      }

      error.value = 'Respuesta inválida del servidor'
      return false
    } catch (err: any) {
      error.value = describeLoginFailure(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Impersonate (requiere backend ENABLE_IMPERSONATION=1)
  async function impersonate(params: { userId?: number; email?: string }): Promise<boolean> {
    try {
      isLoading.value = true
      error.value = null
      const response = await ApiClient.post<AuthResponse>('/auth/impersonate', params)
      const payload = response?.data

      if (applySessionResponse(payload)) {
        try {
          await websocketService.connect()
          if (user.value) websocketService.subscribeUserChannels(user.value)
          const notif = useNotificationsStore()
          notif.initialize().catch(() => {})
        } catch {}

        schedulePostLoginPreload(500)

        return true
      }

      error.value = 'Respuesta inválida del servidor'
      return false
    } catch (err: any) {
      error.value = err.message || 'Error en impersonate'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // 🚪 Función de Logout (manual, canónico) — P12-TEST1A-R3 + R3A.
  // Ownership is scoped to the session generation that OWNED the logout at invocation:
  //  · same generation → concurrent/repeated logout() reuse ONE in-flight promise / ONE remote POST.
  //  · different generation → a newer session's logout can never reuse an older generation's
  //    pending promise, and issues its own POST.
  //  · an old generation's termination that settles late only tears down local state when THAT
  //    exact generation is still current — it must never clear a newer session (R3A race fix).
  // Capture at invocation: the generation, whether that generation held a session, and an
  // in-flight record scoped to that generation.
  async function logout(): Promise<void> {
    const generation = sessionGeneration
    const existing = terminationsByGeneration.get(generation)
    if (existing) return existing

    const hadSession = !!token.value

    const termination = (async () => {
      try {
        if (hadSession) {
          await ApiClient.post('/auth/logout')
        }
      } catch (err) {
        console.warn('⚠️ Error al notificar logout al servidor:', err)
      } finally {
        // Only tear down when THIS generation is still current; a newer session must survive.
        if (sessionGeneration === generation) {
          clearSessionState()
          console.log('✅ Logout completado')
        } else {
          console.log('ℹ️ Logout de una generación previa ignorado; la sesión ya fue reemplazada')
        }
      }
    })()

    terminationsByGeneration.set(generation, termination)
    try {
      await termination
    } finally {
      terminationsByGeneration.delete(generation)
    }
  }

  // P12-TEST1A-R3 + R3A: cierre de sesión FORZADO por el servidor (evento auth:force_logout). La
  // sesión remota YA fue revocada por el backend, por lo que este camino NO emite POST
  // /auth/logout (elimina el 401 de eco). Reutiliza el ÚNICO teardown local canónico
  // (clearSessionState), que además AVANZA la generación de sesión — invalidando atómicamente la
  // generación actual, de modo que trabajo asíncrono viejo (p. ej. un POST de logout en vuelo) no
  // pueda limpiar una sesión futura. Es idempotente: invocarlo repetidamente (eventos duplicados)
  // es inocuo y nunca resucita la autoridad de sesión. No decodifica el JWT ni crea otro store.
  function handleServerForcedLogout(): void {
    clearSessionState()
    console.log('✅ Sesión cerrada por el servidor (teardown local canónico)')
  }

  // MGI-PILOT-SELF-SERVICE-CREDENTIAL-ROTATION-WS1 · the caller rotates ITS OWN credential. The request carries only
  // the two passwords — never an identity: the backend takes the target from the session. On success the backend has
  // ALREADY revoked every session of this identity (and evicts its sockets), so — exactly as for a server-forced
  // logout — the local teardown is the canonical one and NO POST /auth/logout is sent (it would echo a 401). The
  // passwords are neither stored nor logged here.
  // WS1-R2 · a refusal travels the canonical client's NORMAL error path (no validateStatus override): a 401 — the
  // server no longer admits this session (revoked, deactivated, or its role/session drifted) — reaches the canonical 401
  // interceptor, which clears the stored session and sends the user to /login; the in-memory session is torn down by the
  // same canonical local teardown, so nothing authenticated outlives it. 400/403/429 are the user's to read: they are
  // returned inline and tear nothing down (a 403 is only marked access-denied; it never redirects by default).
  async function rotateOwnCredential(currentPassword: string, newPassword: string): Promise<SelfRotationOutcome> {
    let status = 0
    let code: string | null = null
    try {
      const response = await apiClient.post(SELF_ROTATION_ENDPOINT, { currentPassword, newPassword }, { timeout: 15000 })
      status = response.status
      code = (response.data as any)?.code ?? null
    } catch (err: any) {
      status = err?.status ?? err?.response?.status ?? 0
      code = err?.response?.data?.code ?? null
    }
    if (status === 200) {
      handleServerForcedLogout()
      return { ok: true }
    }
    if (status === 401) handleServerForcedLogout()
    return { ok: false, status, code }
  }

  // 🔄 PR-02: Restaurar bootstrap local y confirmar continuidad de sesión
  // únicamente contra /api/auth/validate antes de considerarla vigente.
  function restoreSession(): boolean {
    try {
      const savedToken = localStorage.getItem('mgi_auth_token')
      const savedUser = localStorage.getItem('mgi_user_data')
      const savedTokenTimestamp = localStorage.getItem('mgi_token_timestamp')

      if (savedToken && savedUser) {
        const parsedUser = JSON.parse(savedUser)
        token.value = savedToken
        user.value = {
          ...parsedUser,
          role: mapBackendRoleToFrontend(parsedUser?.role)
        }
        tokenTimestamp.value = savedTokenTimestamp ? Number(savedTokenTimestamp) : null
        sessionAuthority.value = 'restoring'

        console.log('✅ Bootstrap local restaurado; pendiente validación canónica:', user.value?.fullName)

        void ensureRestoredSessionValidated()

        return true
      }

      if (savedToken || savedUser || savedTokenTimestamp) {
        clearSessionState()
      }

      return false
    } catch (err) {
      console.error('❌ Error restaurando sesión:', err)
      clearSessionState()
      return false
    }
  }

  // 🔄 PR-02 + R3B: Verificar validez del token contra /api/auth/validate (contrato canónico), con
  // propiedad de validación scoped por generación de sesión.
  //
  // Ejecuta el request real y sólo muta estado si la generación que lo inició SIGUE siendo la
  // actual. Un resultado obsoleto (stale) — su sesión ya fue reemplazada/terminada — no aplica
  // identidad ni limpia nada de la sesión vigente (que pertenece a otra generación).
  async function runValidation(generation: number): Promise<ValidationOutcome> {
    try {
      const response = await apiClient.get('/auth/validate', {
        timeout: 5000,
        validateStatus: () => true
      })

      if (sessionGeneration !== generation) {
        return 'stale'
      }

      if (response.status === 401 || response.status === 403) {
        console.warn('⚠️ validateToken: backend rechazó token')
        clearSessionState()
        return 'invalid'
      }

      const data = response.data
      if (data?.success) {
        return applyValidatedSession(data) ? 'valid' : 'invalid'
      }

      clearSessionState()
      return 'invalid'
    } catch (err) {
      if (sessionGeneration !== generation) {
        return 'stale'
      }
      console.error('❌ validateToken: error de red:', err)
      clearSessionState()
      return 'invalid'
    }
  }

  // Interno: resuelve el OUTCOME completo de la validación de la generación actual. Los llamadores
  // que deben distinguir un resultado inválido-actual (que SÍ limpió la sesión) de uno obsoleto
  // (stale: su generación ya fue reemplazada, no limpió nada) usan esto; validateToken() lo colapsa
  // al booleano público.
  async function validateSession(): Promise<ValidationOutcome> {
    const generation = sessionGeneration

    if (!token.value) {
      sessionAuthority.value = 'anonymous'
      return 'invalid'
    }

    // Same-generation dedupe: concurrent callers share ONE /auth/validate request.
    let validation = validationsByGeneration.get(generation)
    if (!validation) {
      validation = runValidation(generation)
      validationsByGeneration.set(generation, validation)
      // Cleanup removes ONLY this generation's record (generations are monotonic, never reused).
      void validation.finally(() => {
        validationsByGeneration.delete(generation)
      })
    }

    return validation
  }

  // Contrato público sin cambios: validateToken(): Promise<boolean>. `true` sólo cuando ESTA
  // generación quedó confirmada como válida; un resultado stale devuelve `false` SIN mutar nada
  // (ningún llamador interpreta ese `false` como "sesión actual inválida": ensureRestored… ahora
  // distingue el outcome y canonicalGuard descarta el booleano y re-lee isAuthenticated).
  async function validateToken(): Promise<boolean> {
    return (await validateSession()) === 'valid'
  }

  async function waitForSessionRestore(): Promise<boolean> {
    if (sessionAuthority.value !== 'restoring') {
      return isAuthenticated.value
    }

    return ensureRestoredSessionValidated()
  }

  // 📊 Actualizar datos del usuario
  async function updateProfile(updates: Partial<User>): Promise<boolean> {
    if (!user.value) return false

    try {
      isLoading.value = true

      const response = await ApiClient.put('/auth/profile', updates)

      if (response.data) {
        user.value = { ...user.value, ...response.data }
        localStorage.setItem('mgi_user_data', JSON.stringify(user.value))

        console.log('✅ Perfil actualizado')
        return true
      }

      return false
    } catch (err: any) {
      error.value = err.message || 'Error actualizando perfil'
      console.error('❌ Error actualizando perfil:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // 🔄 Cambio seguro de usuario/rol - Limpia sesión anterior completamente
  async function switchUser(roleId: string): Promise<boolean> {
    if (!TEST_SESSION_TOOLS_ENABLED) {
      error.value = 'El cambio de usuario de laboratorio esta deshabilitado en preproduccion'
      return false
    }

    try {
      console.log('🔄 Cambiando usuario/rol:', roleId)

      await logout()
      await new Promise(resolve => setTimeout(resolve, 100))

      return await loginWithTestToken(roleId)
    } catch (err: any) {
      console.error('❌ Error cambiando usuario:', err)
      error.value = err.message || 'Error cambiando usuario'
      return false
    }
  }

  // 🧪 Login con token de prueba para testing
  async function loginWithTestToken(roleId: string): Promise<boolean> {
    if (!TEST_SESSION_TOOLS_ENABLED) {
      error.value = 'Las sesiones de prueba no forman parte del corredor preproduccion'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const labCredentials: Record<UserRole, LoginCredentials> = {
        super_admin: { email: 'admin@mgi.com', password: 'admin123' },
        administration_manager: { email: 'adminmgr@mgi.com', password: 'adminmgr123' },
        group_manager: { email: 'manager@mgi.com', password: 'manager123' },
        field_supervisor: { email: 'supervisor@mgi.com', password: 'supervisor123' },
        route_operator: { email: 'operator@mgi.com', password: 'operator123' },
        agency_employee: { email: 'employee@mgi.com', password: 'employee123' },
        department_head: { email: 'analyst@mgi.com', password: 'analyst123' }
      }

      const credentials = labCredentials[mapBackendRoleToFrontend(roleId)]
      if (!credentials) {
        throw new Error('Rol de laboratorio no encontrado')
      }

      const response = await ApiClient.post<AuthResponse>('/auth/test-login', credentials)
      if (applySessionResponse(response?.data)) {
        return true
      }

      throw new Error('Respuesta inválida del servidor de laboratorio')
    } catch (err: any) {
      error.value = err.message || 'Error en el login de laboratorio'
      console.error('❌ Error de login de laboratorio:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // 🎯 Inicialización del store
  function initialize() {
    restoreSession()
  }

  return {
    // Estado
    user,
    token,
    isLoading,
    error,
    sessionAuthority,
    session,

    // Computed
    isAuthenticated,
    userRole,
    userPermissions,
    userName,
    hasRole,
    hasPermission,
    availableActions,
    assignableRoles,
    canPerform,
    canAssignRole,
    canAccessDashboard,
    isAdminRole,
    isOperationalRole,
    isAnalyticalRole,
    getDashboardRoute,

    // Acciones
    login,
    loginReal,
    impersonate,
    loginWithTestToken,
    switchUser,
    logout,
    handleServerForcedLogout,
    rotateOwnCredential,
    restoreSession,
    validateToken,
    waitForSessionRestore,
    updateProfile,
    initialize
  }
})
